import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { HStack } from '@cbhq/cds-web/layout';
import { TextTitle3, TextTitle4 } from '@cbhq/cds-web/typography';

import { useNavigationContext } from ':cloud/contexts/NavigationContext';
import { useProject } from ':cloud/contexts/ProjectContext';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { AppRoute } from ':cloud/utils/routes';
import { NavigationBar } from ':cloud/widgets/navigation/NavigationBar';
import { ProjectSelector } from ':cloud/widgets/projects/ProjectSelector';

const titleMap: Record<string, string> = {
  [AppRoute.Access.OAuthCreate]: 'Create Client',
  [AppRoute.Access.OAuthEdit]: 'Edit Client',
};

export function TopNav() {
  const { activePage } = useNavigationContext();
  const { selectedProject } = useProject();
  const history = useHistory();
  const location = useLocation();
  const pathArray = location.pathname.split('/');
  const { isPhone } = useSimpleBreakpoints();
  const isOAuthUpsertClientRoute =
    location.pathname.includes(AppRoute.Access.OAuthCreate) ||
    location.pathname.includes(AppRoute.Access.OAuthEdit);

  const handleBackPress = useCallback(() => {
    if (location.pathname.includes('participate')) {
      history.push(AppRoute.Participate.Home);
    } else {
      history.go(-1);
    }
  }, [history, location.pathname]);

  const showBackButton = useMemo(
    (): boolean =>
      !isPhone &&
      ((pathArray.length > 2 && location.pathname.includes(AppRoute.Participate.Home)) ||
        isOAuthUpsertClientRoute ||
        location.pathname.includes('base-l3/deploy')),
    [isOAuthUpsertClientRoute, isPhone, location.pathname, pathArray.length],
  );

  const renderTitle = useMemo(() => {
    if (activePage.id === 'project-dashboard' && isPhone) {
      return selectedProject?.name;
    }

    if (activePage.id === 'project-dashboard') {
      return <ProjectSelector />;
    }

    if (isPhone) {
      return <TextTitle3 as="h1">{titleMap[location.pathname] || activePage.title}</TextTitle3>;
    }

    return (
      <HStack alignItems="center">
        <ProjectSelector compact />
        <HStack alignItems="center" gap={1} spacingStart={1}>
          <TextTitle4 dangerouslySetColor="rgb(var(--gray10))" as="span">
            /
          </TextTitle4>
          <TextTitle3 as="h1">{titleMap[location.pathname] || activePage.title}</TextTitle3>
        </HStack>
      </HStack>
    );
  }, [activePage, location.pathname, isPhone, selectedProject?.name]);

  return (
    <NavigationBar
      handleBackPress={handleBackPress}
      showBackButton={showBackButton}
      title={renderTitle}
    />
  );
}
